import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, DropdownMenu, Text } from '@fly/core';
import { Platform, PlatformInfo, routes } from '@fly/shared';
import { useIsMobileDevice } from '@fly/shared-hooks/useIsMobileDevice';
import { FlyCopyButton } from '@fly/shared-ui';
import cn from 'classnames';

import { getPlatformIcon } from '../PlatformIcons/PlatformIcons';
import styles from './CliInstallCommand.module.less';

const INSTALL_URL = 'https://fly.jfrog.ai/download/desktop';

interface PlatformCommand {
  prefix: string;
  url: string;
  suffix: string;
}

const PLATFORM_COMMANDS: Record<string, PlatformCommand> = {
  [Platform.DARWIN]: {
    prefix: 'curl -fsSL',
    url: INSTALL_URL,
    suffix: '| bash',
  },
  [Platform.WIN32]: {
    prefix: 'powershell -c "irm',
    url: INSTALL_URL,
    suffix: '| iex"',
  },
};

function getFullCommand(command: PlatformCommand): string {
  return `${command.prefix} ${command.url} ${command.suffix}`;
}

const PLATFORM_LABELS: Record<string, string> = {
  [Platform.DARWIN]: 'macOS',
  [Platform.WIN32]: 'Windows',
};

const SELECTABLE_PLATFORMS = [Platform.DARWIN, Platform.WIN32] as const;

interface CliInstallCommandProps {
  platform: Platform;
  detectedConfig: PlatformInfo | null;
  onDownload: (config: PlatformInfo) => void;
  /**
   * Where the "Get Started" button points. Defaults to the in-app sign-up
   * flow; cross-origin callers (e.g. the landing page) pass an absolute URL.
   */
  signupUrl?: string;
}

function PlatformSelector({
  selected,
  onSelect,
}: {
  selected: Platform;
  onSelect: (p: Platform) => void;
}) {
  return (
    <DropdownMenu
      trigger={
        <Button
          size='4'
          color='neutral'
          highContrast
          className='shrink-0'
          iconStart={getPlatformIcon(selected, { size: 20 })}
          iconEnd='chevron_down'
          data-testid='platform-selector'
        >
          <Text size='4' className='font-semibold'>
            {PLATFORM_LABELS[selected] ?? selected}
          </Text>
        </Button>
      }
      items={SELECTABLE_PLATFORMS.filter((p) => p !== selected).map((p) => ({
        title: PLATFORM_LABELS[p],
        iconStart: getPlatformIcon(p, { size: 16 }),
        onClick: () => onSelect(p),
      }))}
    />
  );
}

function CommandBar({
  command,
  selectedPlatform,
  onSelectPlatform,
  className,
}: {
  command: PlatformCommand;
  selectedPlatform: Platform;
  onSelectPlatform: (p: Platform) => void;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div
      className={cn(
        'bg-grayA-3 border-grayA-6 gap-4 rounded-2xl py-1 pl-1 pr-4 backdrop-blur-xs flex w-full items-center border',
        styles.commandBar,
        copied && styles.glow,
        className
      )}
      onAnimationEnd={() => setCopied(false)}
    >
      <div className={styles.glowRing} aria-hidden='true' />

      <PlatformSelector
        selected={selectedPlatform}
        onSelect={onSelectPlatform}
      />

      <div className='min-w-0 gap-4 flex flex-1 items-center'>
        <code className='gap-4 text-slateA-11 leading-7 flex items-center text-[18px] tracking-[-0.08px] whitespace-nowrap'>
          <span className='font-semibold'>{command.prefix}</span>
          <span className='font-semibold'>{command.url}</span>
          <span className='font-semibold'>{command.suffix}</span>
        </code>
      </div>

      <FlyCopyButton
        code={getFullCommand(command)}
        size={16}
        copyTimeoutMs={3000}
        onClick={() => setCopied(true)}
        data-testid='cli-install-copy'
      />
    </div>
  );
}

export function CliInstallCommand({
  platform,
  detectedConfig,
  onDownload,
  signupUrl = routes.SIGN_UP.PROVIDERS,
}: CliInstallCommandProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobileDevice();

  const initialPlatform =
    platform in PLATFORM_COMMANDS ? platform : Platform.DARWIN;
  const [selectedPlatform, setSelectedPlatform] = useState(initialPlatform);
  const command = PLATFORM_COMMANDS[selectedPlatform];

  // Mobile/tablet devices can't run the desktop CLI installer or app, so send
  // them to the sign-up flow instead of the (useless) download command.
  if (isMobile) {
    return (
      <div
        className='gap-3 flex flex-col items-center'
        data-testid='cli-install-command'
      >
        <Button
          size='4'
          variant='solid'
          color='neutral'
          href={signupUrl}
          data-testid='cli-install-get-started'
          highContrast
          iconEnd='single_arrow_right'
        >
          <Text size='4' className='text-neutral-11'>
            {t('app.home.get_started', { defaultValue: 'Get Started' })}
          </Text>
        </Button>
      </div>
    );
  }

  // Non-mobile devices: viewport width decides the presentation. Below `md` the
  // command bar doesn't fit, so show a plain download button; at `md` and up
  // show the full copyable install command plus a download link.
  return (
    <div
      className='gap-3 flex flex-col items-center'
      data-testid='cli-install-command'
    >
      <CommandBar
        command={command}
        selectedPlatform={selectedPlatform}
        onSelectPlatform={setSelectedPlatform}
        className='md:flex hidden'
      />
      <Button
        size='4'
        variant='solid'
        color='neutral'
        iconEnd='download'
        onClick={() => detectedConfig && onDownload(detectedConfig)}
        data-testid='download-fallback-mobile'
        highContrast
        className='md:hidden flex'
      >
        <Text size='4' className='text-neutral-11'>
          {t('app.home.click_to_download', {
            defaultValue: 'Click to download',
          })}
        </Text>
      </Button>
      <Button
        size='4'
        variant='ghost'
        color='neutral'
        iconEnd='download'
        onClick={() => detectedConfig && onDownload(detectedConfig)}
        data-testid='download-fallback-desktop'
        highContrast
        className='md:flex hidden'
      >
        <Text size='4' className='text-neutral-11'>
          {t('app.home.or_download', {
            defaultValue: 'Or click to download',
          })}
        </Text>
      </Button>
    </div>
  );
}
